<template>
  <v-container fluid class="pa-2 interview-analysis-container" style="overflow: visible;">
    <!-- Loading state -->
    <div v-if="loading" class="text-center my-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="mt-4">Loading analysis data...</p>
    </div>

    <!-- Error state -->
    <div v-if="error" class="text-center my-12">
      <v-icon large color="error">mdi-alert-circle</v-icon>
      <p class="error--text mt-4">{{ error }}</p>
      <v-btn color="primary" @click="fetchAnalysisData">Retry</v-btn>
    </div>

    <!-- Main content -->
    <div v-if="!loading && !error && analysisData" :key="analysisContentKey">
      <CoherenceAnalysisTab
        v-if="analysisData"
        :key="`coherence-${analysisContentKey}`"
        class="mb-3"
        :interview-id="analysisKey"
        :analysis-data="analysisData"
        :candidate-info="candidateOverlayInfo"
        :hide-coherence="!featureFlags.coherence"
        :hide-share="!featureFlags.shareReport"
        @analysis-complete="onAnalysisComplete"
        @select-run="onSelectRun"
        @run-changed="onRunChanged"
        @launch-engine="launchWithEngine"
      />

      <!-- Tabs -->
      <v-tabs v-model="activeTab" background-color="transparent" class="mb-3">
        <v-tab v-for="tab in secondaryTabs" :key="tab.key">
          <v-icon left>{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="tabs-items-container">
        <v-tab-item
          v-for="tab in secondaryTabs"
          :key="`${analysisContentKey}-${tab.key}`"
          class="tab-item-container"
        >
          <template v-if="tab.key === 'behaviours'">
          <div v-if="beliefsLoading" class="text-center my-12">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-4">Loading pre competency analysis...</p>
          </div>
          <div v-else-if="beliefsError" class="text-center my-12">
            <v-icon large color="warning">mdi-alert</v-icon>
            <p class="mt-4">Pre competency analysis not available for this interview.</p>
          </div>
          <PreCompetencyTab
            v-else-if="isCompetencyAnalysisReady"
            :interview-id="analysisKey"
            :analysis-data="analysisData"
            :beliefs-analysis="beliefsAnalysis"
          />
          <EngineEmptyState
            v-else
            engine="pre_competency"
            label="pre competency"
            v-bind="engineEmptyProps('pre_competency')"
            @launch="launchWithEngine"
          />
          </template>

          <template v-else-if="tab.key === 'cultural'">
          <div v-if="beliefsLoading" class="text-center my-12">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-4">Loading cultural fit analysis...</p>
          </div>
          <div v-else-if="beliefsError" class="text-center my-12">
            <v-icon large color="warning">mdi-alert</v-icon>
            <p class="mt-4">Cultural fit analysis not available for this interview.</p>
          </div>
          <CulturalFitTab
            v-else-if="isCulturalFitReady"
            :analysis-data="analysisData"
            :beliefs-analysis="beliefsAnalysis"
          />
          <EngineEmptyState
            v-else
            engine="cultural_fit"
            label="cultural fit"
            v-bind="engineEmptyProps('cultural_fit')"
            @launch="launchWithEngine"
          />
          </template>

          <template v-else-if="tab.key === 'client-questions'">
          <div v-if="beliefsLoading" class="text-center my-12">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-4">Loading client questions...</p>
          </div>
          <div v-else-if="beliefsError" class="text-center my-12">
            <v-icon large color="warning">mdi-alert</v-icon>
            <p class="mt-4">Client questions are not available for this interview.</p>
          </div>
          <ClientQuestionsTab
            v-else-if="isClientQuestionsReady"
            :interview-id="analysisKey"
            :beliefs-analysis="beliefsAnalysis"
          />
          <EngineEmptyState
            v-else
            engine="client_questions"
            label="client questions"
            v-bind="engineEmptyProps('client_questions')"
            @launch="launchWithEngine"
          />
          </template>

          <template v-else-if="tab.key === 'toolkit'">
          <div v-if="beliefsLoading" class="text-center my-12">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-4">Loading toolkit...</p>
          </div>
          <div v-else-if="beliefsError" class="text-center my-12">
            <v-icon large color="warning">mdi-alert</v-icon>
            <p class="mt-4">Toolkit not available for this interview.</p>
          </div>
          <ToolkitTripleATab
            v-else-if="isToolkitReady"
            :beliefs-analysis="beliefsAnalysis"
          />
          <EngineEmptyState
            v-else
            engine="toolkit"
            label="toolkit"
            v-bind="engineEmptyProps('toolkit')"
            @launch="launchWithEngine"
          />
          </template>

          <template v-else-if="tab.key === 'raw-json'">
          <v-card class="pa-4">
            <v-card-title class="primary--text">
              <v-icon left color="primary">mdi-code-json</v-icon>
              Full analysis data (raw)
            </v-card-title>
            <v-card-text>
              <p class="caption grey--text mb-3">
                Complete payload from metadata, gestures, transcription and beliefs endpoints. Click objects/arrays to expand or collapse.
              </p>
              <div class="raw-json-container">
                <JsonTreeViewer v-if="rawAnalysisPayload && Object.keys(rawAnalysisPayload).length" :data="rawAnalysisPayload" />
                <pre v-else class="raw-json-pre">{{ rawAnalysisJson }}</pre>
              </div>
            </v-card-text>
          </v-card>
          </template>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <LaunchAnalysisDialog
      v-model="launchDialog.open"
      :source-type="launchSourceType"
      :targets="launchDialog.targets"
      :initial-engines="launchDialog.initialEngines"
      :analysis-types="launchDialog.analysisTypes"
      :loading-analysis-types="launchDialog.loadingTypes"
      :socket-id="$socket && $socket.id ? $socket.id : ''"
      @launched="onEngineLaunchQueued"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ClientQuestionsTab, {
  isClientQuestionsAvailable,
} from '../components/analysis/ClientQuestionsTab.vue';
import CulturalFitTab from '../components/analysis/CulturalFitTab.vue';
import JsonTreeViewer from '../components/analysis/JsonTreeViewer.vue';
import PreCompetencyTab from '../components/analysis/PreCompetencyTab.vue';
import ToolkitTripleATab from '../components/analysis/ToolkitTripleATab.vue';
import CoherenceAnalysisTab from '../components/analysis/CoherenceAnalysisTab.vue';
import EngineEmptyState from '@/components/analysis/runs/EngineEmptyState.vue';
import LaunchAnalysisDialog from '@/components/analysis/runs/LaunchAnalysisDialog.vue';
import featureFlags from '@/config/featureFlags';
import InterviewsDataService from '@/services/InterviewsDataService';
import { analysisKey as buildAnalysisKey } from '@/store/modules/analysisData';

export default {
  name: 'InterviewAnalysis',

  components: {
    ClientQuestionsTab,
    CulturalFitTab,
    EngineEmptyState,
    JsonTreeViewer,
    LaunchAnalysisDialog,
    PreCompetencyTab,
    ToolkitTripleATab,
    CoherenceAnalysisTab,
  },

  props: {
    isManualAnalysis: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Tab state
      activeTab: 0,

      // Data state
      loading: true,
      error: null,
      beliefsAttempted: false, // Track if we've tried to load beliefs
      viewRenderNonce: 0,

      // 'Launch with this engine' from an EngineEmptyState (M3 F8)
      launchDialog: {
        open: false,
        targets: [],
        initialEngines: [],
        analysisTypes: [],
        loadingTypes: false,
      },

      // Video state
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      playbackRate: 1,
      volume: 100,
      isMuted: false,
      isLoadingVideo: true,
      hasVideoError: false,

      // UI state
      showControls: true,
      controlsTimeout: null,
      showVolumeSlider: false,
      volumeHoverTimeout: null,
      isDragging: false,
      isFullscreen: false,

      // Options
      speedOptions: [
        { value: 0.5, text: '0.5x' },
        { value: 0.75, text: '0.75x' },
        { value: 1, text: 'Normal' },
        { value: 1.25, text: '1.25x' },
        { value: 1.5, text: '1.5x' },
        { value: 2, text: '2x' },
      ],

      // Gesture table headers
      gestureHeaders: [
        { text: 'Time', value: 'time', sortable: true },
        { text: 'Speaking', value: 'is_speaking', sortable: false },
        { text: 'Alert', value: 'alert', sortable: true },
        { text: 'Direction', value: 'direction', sortable: false },
        { text: 'Head Pose', value: 'head_pose', sortable: false },
        { text: 'Lips', value: 'lips', sortable: false },
        { text: 'Brows', value: 'brows', sortable: false },
      ],
    };
  },

  computed: {
    ...mapGetters('analysisData', [
      'getMetadata',
      'getGestures',
      'getCoherenceAlerts',
      'getCoherenceSemanticModes',
      'getGestureTableRows',
      'getTranscription',
      'getBeliefs',
      'isLoadingInitial',
      'isLoadingBeliefs',
      'hasBeliefs',
      'hasError',
      'getError',
      'getEnginesRun',
      'getEngineSkipReason',
    ]),

    /** Engine selection of the viewed run ({requested, executed, skipped, partial, known}). */
    enginesRun() {
      return this.getEnginesRun(this.analysisKey);
    },

    /** Source type of the launch dialog opened from an EngineEmptyState. */
    launchSourceType() {
      return this.isManualAnalysisFromRoute ? 'manual_analysis' : 'interview';
    },

    interviewId() {
      // Convert to Number to match prop type in analysis tabs
      const id = this.$route.params.id;
      return typeof id === 'string' ? parseInt(id, 10) : id;
    },

    /** Run selected through the route (`?run=<run_id>`); null follows the active pointer. */
    runId() {
      const raw = this.$route.query?.run;
      return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
    },

    sourceType() {
      return this.isManualAnalysisFromRoute ? 'manual_analysis' : 'interview';
    },

    /** Composite store key (M2): every store slice of this view is cached under it. */
    analysisKey() {
      return buildAnalysisKey(this.sourceType, this.interviewId, this.runId);
    },

    // Determine if this is a manual analysis from route name (more reliable than prop)
    isManualAnalysisFromRoute() {
      return this.$route.name === 'manual analysis' || this.isManualAnalysis;
    },

    // Build analysisData object from store for child components (new ai-service structure)
    analysisData() {
      const metadata = this.getMetadata(this.analysisKey);
      const gestureTableRows = this.getGestureTableRows(this.analysisKey);
      const transcription = this.getTranscription(this.analysisKey);

      if (!metadata) return null;

      return {
        videoUrl: metadata.videoUrl,
        interviewInfo: metadata.interviewInfo,
        landmarksConfig: metadata.landmarksConfig,
        analysisStatus: metadata.analysisStatus,
        analysisProgress: metadata.analysisProgress,
        isManualAnalysis: metadata.isManualAnalysis,
        curriculumSummary: metadata.curriculumSummary ?? null,
        gestureAnalysis: gestureTableRows,
        transcription: transcription,
        coherenceAlerts: this.getCoherenceAlerts(this.analysisKey),
        coherenceSemanticModes: this.getCoherenceSemanticModes(this.analysisKey),
      };
    },

    // Get beliefs from store (loaded lazily when needed)
    beliefsAnalysis() {
      return this.getBeliefs(this.analysisKey);
    },

    fullInterviewResult() {
      return this.beliefsAnalysis?.fullInterviewResult || null;
    },

    gestureAnalysis() {
      return this.getGestureTableRows(this.analysisKey) || [];
    },

    gestureDataReady() {
      const g = this.getGestures(this.analysisKey);
      return !!g && ( (g.alerts && g.alerts.length > 0) || (g.episodes && g.episodes.length > 0) );
    },

    candidateName() {
      return (
        this.analysisData?.candidateName ||
        this.analysisData?.interviewInfo?.candidateName ||
        'Candidate'
      );
    },

    positionName() {
      return (
        this.analysisData?.positionName ||
        this.analysisData?.interviewInfo?.positionName ||
        'Position not specified'
      );
    },

    interviewDate() {
      return (
        this.analysisData?.interviewDate || this.analysisData?.interviewInfo?.interviewDate || null
      );
    },

    status() {
      return this.analysisData?.status || this.analysisData?.interviewInfo?.status || null;
    },

    // Profiler Report data
    profilerReport() {
      return (
        this.beliefsAnalysis?.beliefsAnalysis?.profilerReport ||
        this.beliefsAnalysis?.profilerReport ||
        null
      );
    },

    curriculumSummary() {
      return (
        this.analysisData?.curriculumSummary ||
        this.fullInterviewResult?.results?.pre_competencial?.curriculum_summary ||
        this.beliefsAnalysis?.beliefsAnalysis?.competencialAnalysis?.curriculum_summary ||
        this.fullInterviewResult?.results?.pre_competencial?.about ||
        this.profilerReport?.curriculum_summary ||
        this.profilerReport?.about ||
        null
      );
    },

    competencyGlobalForHeader() {
      const pre = this.fullInterviewResult?.results?.pre_competencial;
      if (pre?.global_match != null && pre.global_match !== '') return Number(pre.global_match);
      const legacy = this.beliefsAnalysis?.beliefsAnalysis?.competencialAnalysis?.global_match?.score;
      if (legacy != null && legacy !== '') return Number(legacy);
      return null;
    },

    culturalGlobalForHeader() {
      const c = this.fullInterviewResult?.results?.cultural_fit?.match;
      if (c != null && c !== '') return Number(c);
      const cult = this.beliefsAnalysis?.beliefsAnalysis?.culturalAnalysis;
      const flat = cult?.match ?? cult?.global_cultural_match ?? cult?.globalCulturalMatch;
      if (flat != null && flat !== '') return Number(flat);
      const nested = cult?.beliefs?.global_match ?? cult?.beliefs?.globalMatch;
      if (nested != null && nested !== '') return Number(nested);
      return null;
    },

    headerTotalScoreLabel() {
      const a = this.competencyGlobalForHeader;
      const b = this.culturalGlobalForHeader;
      if (
        a == null ||
        b == null ||
        Number.isNaN(Number(a)) ||
        Number.isNaN(Number(b))
      ) {
        return 'N/A';
      }
      return `${Math.round((Number(a) + Number(b)) / 2)}%`;
    },

    /** 0–100 for header colour bands; null when total score is N/A */
    headerTotalScorePercent() {
      const a = this.competencyGlobalForHeader;
      const b = this.culturalGlobalForHeader;
      if (
        a == null ||
        b == null ||
        Number.isNaN(Number(a)) ||
        Number.isNaN(Number(b))
      ) {
        return null;
      }
      return Math.round((Number(a) + Number(b)) / 2);
    },

    globalMatchScore() {
      // Try profilerReport first, then fall back to competencialAnalysis
      if (this.profilerReport?.global_match !== undefined) {
        return Math.round(this.profilerReport.global_match);
      }
      if (
        this.beliefsAnalysis?.beliefsAnalysis?.competencialAnalysis?.global_match?.score !==
        undefined
      ) {
        return Math.round(this.beliefsAnalysis.beliefsAnalysis.competencialAnalysis.global_match.score);
      }
      return 0;
    },

    isSuitable() {
      if (this.profilerReport?.suitable !== undefined) {
        return this.profilerReport.suitable;
      }
      return this.globalMatchScore >= 70;
    },

    isGestureAnalysisReady() {
      return this.gestureDataReady;
    },

    isCompetencyAnalysisReady() {
      if (this.fullInterviewResult?.results?.pre_competencial) return true;
      return !!(
        this.beliefsAnalysis?.beliefsAnalysis?.competencialAnalysis &&
        (this.beliefsAnalysis.beliefsAnalysis.competencialAnalysis.global_match ||
          this.beliefsAnalysis.beliefsAnalysis.competencialAnalysis.information_per_behaviour ||
          this.beliefsAnalysis.beliefsAnalysis.competencialAnalysis.detailed_analysis)
      );
    },

    isCulturalFitReady() {
      if (this.fullInterviewResult?.results?.cultural_fit) return true;
      return !!(
        this.beliefsAnalysis?.beliefsAnalysis?.culturalAnalysis?.beliefs &&
        (this.beliefsAnalysis.beliefsAnalysis.culturalAnalysis.beliefs.global_match ||
          this.beliefsAnalysis.beliefsAnalysis.culturalAnalysis.beliefs.order_match ||
          this.beliefsAnalysis.beliefsAnalysis.culturalAnalysis.beliefs.open_questions)
      );
    },

    isClientQuestionsReady() {
      // V10 split: client questions have their own stage/result; legacy docs kept them in
      // cultural fit (see isClientQuestionsAvailable).
      return isClientQuestionsAvailable({
        fullInterviewResult: this.fullInterviewResult,
        enginesRun: this.enginesRun,
        culturalFitReady: this.isCulturalFitReady,
      });
    },

    isToolkitReady() {
      if (this.fullInterviewResult?.results?.triple_a_toolkit) return true;
      return !!(
        this.beliefsAnalysis?.beliefsAnalysis?.profilerReport?.toolkit &&
        Object.keys(this.beliefsAnalysis.beliefsAnalysis.profilerReport.toolkit).length > 0
      );
    },

    beliefsLoading() {
      return this.isLoadingBeliefs(this.analysisKey);
    },

    beliefsError() {
      // Beliefs failed to load (hasBeliefs check after loading finished). A partial run (engines
      // switched off) legitimately has no beliefs: let the engine empty states explain it.
      if (this.enginesRun.known && this.enginesRun.partial) return false;
      return !this.beliefsLoading && this.beliefsAttempted && !this.hasBeliefs(this.analysisKey);
    },

    rawAnalysisPayload() {
      const meta = this.getMetadata(this.analysisKey);
      const gestures = this.getGestures(this.analysisKey);
      const transcription = this.getTranscription(this.analysisKey);
      const beliefs = this.getBeliefs(this.analysisKey);
      return {
        metadata: meta || null,
        gestures: gestures || [],
        coherence_alerts: this.getCoherenceAlerts(this.analysisKey),
        transcription: transcription || [],
        beliefsAnalysis: beliefs || null,
        aggregatedFullInterviewResult: beliefs?.fullInterviewResult || null,
      };
    },

    rawAnalysisJson() {
      try {
        return JSON.stringify(this.rawAnalysisPayload, null, 2);
      } catch {
        return '{}';
      }
    },

    candidateOverlayInfo() {
      const info = this.analysisData?.interviewInfo || {};
      const durationSecondsRaw = Number(info.analysisDurationSeconds ?? info.duration);
      const durationLabel =
        Number.isFinite(durationSecondsRaw) && durationSecondsRaw > 0
          ? this.formatDurationMinutesSeconds(durationSecondsRaw)
          : 'N/A';
      return {
        candidateName: this.candidateName,
        curriculumSummary: this.curriculumSummary,
        interviewDate: this.formatDateWithTime(this.interviewDate),
        videoDescription: info.description || null,
        analysisDurationLabel: durationLabel,
        status: this.status,
        headerTotalScoreLabel: this.headerTotalScoreLabel,
        headerTotalScorePercent: this.headerTotalScorePercent,
        competencyScoreLabel: this.formatHeaderScore(this.competencyGlobalForHeader),
        culturalScoreLabel: this.formatHeaderScore(this.culturalGlobalForHeader),
        competencyScorePercent: this.roundPercentOrNull(this.competencyGlobalForHeader),
        culturalScorePercent: this.roundPercentOrNull(this.culturalGlobalForHeader),
        isManualAnalysis: this.isManualAnalysisFromRoute,
      };
    },

    analysisContentKey() {
      return `${this.analysisKey}-${this.viewRenderNonce}`;
    },

    availableTabs() {
      const manualTabs = [
        { key: 'coherence', icon: 'mdi-shield-search', label: 'Coherence Analysis' },
        { key: 'gestos', icon: 'mdi-human-handsup', label: 'Non Verbal Analysis' },
        { key: 'raw-json', icon: 'mdi-code-json', label: 'Debug (Raw JSON)' },
      ];

      if (this.isManualAnalysisFromRoute) {
        return manualTabs;
      }

      return [
        ...manualTabs.slice(0, 2),
        { key: 'behaviours', icon: 'mdi-brain', label: 'Pre Competency Analysis' },
        { key: 'cultural', icon: 'mdi-office-building', label: 'Cultural Fit' },
        { key: 'client-questions', icon: 'mdi-account-voice', label: 'Client Questions' },
        { key: 'toolkit', icon: 'mdi-toolbox', label: 'Toolkit Triple A' },
        manualTabs[2],
      ];
    },

    secondaryTabs() {
      return this.availableTabs.filter((tab) => tab.key !== 'coherence' && tab.key !== 'gestos');
    },

    featureFlags() {
      return featureFlags;
    },
  },

  watch: {
    // Covers both a different interview and a different run (`?run=`) of the same one.
    analysisKey(newKey, oldKey) {
      if (!newKey || newKey === oldKey) return;
      this.resetViewStateForInterviewChange(oldKey);
      this.fetchAnalysisData();
    },
  },

  mounted() {
    this.resetViewStateForInterviewChange(null);
    this.fetchAnalysisData();
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
  },

  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    this.clearControlsTimeout();
    this.clearVolumeTimeout();
    // Clear cache when leaving the view
    this.clearAnalysis(this.analysisKey);
  },

  methods: {
    ...mapActions('analysisData', [
      'fetchInitialData',
      'fetchBeliefs',
      'clearAnalysis',
      'refreshMetadata',
    ]),

    /** Props for an EngineEmptyState block: skip reason / failed run of the viewed run. */
    engineEmptyProps(engine) {
      return {
        skipReason: this.getEngineSkipReason(this.analysisKey, engine),
        runStatus: this.enginesRun.status,
        runError: this.enginesRun.error,
      };
    },

    /**
     * 'Launch with this engine': open the launch dialog for this source with the engines of the
     * viewed run plus the requested one preselected (all engines when the run block is unknown).
     */
    async launchWithEngine(payload) {
      const engine = typeof payload?.engine === 'string' ? payload.engine : null;
      this.launchDialog.initialEngines =
        engine && this.enginesRun.known
          ? Array.from(new Set([...this.enginesRun.executed, engine]))
          : [];
      this.launchDialog.targets = [
        {
          id: this.interviewId,
          name: this.candidateName || `${this.isManualAnalysisFromRoute ? 'Video' : 'Interview'} ${this.interviewId}`,
          analysisTypeId: this.analysisData?.interviewInfo?.interviewAnalysisTypeId ?? null,
        },
      ];
      this.launchDialog.open = true;
      if (this.launchDialog.analysisTypes.length) return;
      this.launchDialog.loadingTypes = true;
      try {
        const response = await InterviewsDataService.findAllInterviewAnalysisTypes(1, 100);
        this.launchDialog.analysisTypes = response?.data?.data || [];
      } catch (error) {
        console.error('[InterviewAnalysis] Failed to load analysis configurations:', error);
      } finally {
        this.launchDialog.loadingTypes = false;
      }
    },

    onEngineLaunchQueued(result) {
      const runIds = (result?.accepted || []).map((entry) => entry.run_id).filter(Boolean);
      if (runIds.length && this.$store?._actions?.['analysisQueue/watchRuns']) {
        this.$store.dispatch('analysisQueue/watchRuns', runIds);
      }
      this.launchDialog.targets = [];
      this.launchDialog.initialEngines = [];
    },

    /** RunSelector: update `?run=` (null follows the active pointer); the key watcher reloads. */
    onSelectRun(runId) {
      const query = { ...(this.$route.query || {}) };
      const current = typeof query.run === 'string' && query.run.trim() ? query.run.trim() : null;
      if ((runId || null) === current) return;
      if (runId) query.run = runId;
      else delete query.run;
      this.$router.push({ path: this.$route.path, query }).catch(() => {});
    },

    /** After promote / pin / unpin: refresh the run block (is_active / is_pinned) of this view. */
    async onRunChanged() {
      try {
        await this.refreshMetadata({
          analysisId: this.analysisKey,
          isManual: this.isManualAnalysisFromRoute,
        });
      } catch (error) {
        console.error('[InterviewAnalysis] Failed to refresh run metadata:', error);
      }
    },

    resetViewStateForInterviewChange(previousInterviewId) {
      // Force clean remount for analysis tabs to avoid stale local component state.
      this.viewRenderNonce += 1;

      // Reset local UI state.
      this.activeTab = 0;
      this.error = null;
      this.beliefsAttempted = false;
      this.loading = true;

      this.currentTime = 0;
      this.duration = 0;
      this.isPlaying = false;
      this.playbackRate = 1;
      this.volume = 100;
      this.isMuted = false;
      this.isLoadingVideo = true;
      this.hasVideoError = false;
      this.isDragging = false;
      this.isFullscreen = false;
      this.showVolumeSlider = false;
      this.showControls = true;
      this.clearControlsTimeout();
      this.clearVolumeTimeout();

      if (previousInterviewId != null) {
        this.clearAnalysis(previousInterviewId);
      }
    },

    async fetchAnalysisData() {
      try {
        this.loading = true;
        this.error = null;

        const isManual = this.isManualAnalysisFromRoute;
        // Always start from a clean store snapshot for this analysis id.
        // This avoids stale/partial cached data after route transitions.
        await this.clearAnalysis(this.analysisKey);

        const tasks = [
          this.fetchInitialData({
            analysisId: this.analysisKey,
            isManual: isManual,
          }),
        ];

        // Manual analysis view does not need beliefs payload.
        if (!isManual) {
          tasks.push(this.loadBeliefs());
        }

        await Promise.all(tasks);
      } catch (error) {
        console.error('[InterviewAnalysis] Error fetching analysis data:', error);
        if (!this.getMetadata(this.analysisKey)) {
          this.error = 'Could not load interview analysis';
        }
      } finally {
        this.loading = false;
      }
    },

    async loadBeliefs() {
      if (this.beliefsAttempted) {
        return; // Already tried to load
      }
      
      this.beliefsAttempted = true;
      
      try {
        await this.fetchBeliefs({ 
          analysisId: this.analysisKey, 
          isManual: this.isManualAnalysisFromRoute,
        });
      } catch (error) {
        console.error('[InterviewAnalysis] Error loading beliefs:', error);
        // beliefsError computed will show the error state
      }
    },

    parseTimeToSeconds(timeString) {
      // Parse "00:01:500" format to seconds
      if (!timeString) return 0;
      const parts = timeString.split(':');
      if (parts.length !== 3) return 0;

      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      const milliseconds = parseInt(parts[2], 10) || 0;

      return minutes * 60 + seconds + milliseconds / 1000;
    },

    formatTime(seconds) {
      if (!seconds || !isFinite(seconds) || isNaN(seconds)) {
        return '00:00';
      }

      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    formatDateWithTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return `${dateStr} ${timeStr}`;
    },

    getStatusColor(status) {
      const colorMap = {
        ANALYSED: 'success',
        ANALYSING: 'info',
        COMPLETED: 'success',
        completed: 'success',
        PENDING: 'warning',
        ERROR: 'error',
        IN_PROGRESS: 'info',
      };
      return colorMap[status] || 'grey';
    },

    getScoreColor(score) {
      if (!score && score !== 0) return 'grey';

      // Normalize score to 0-10 scale
      const normalizedScore = score > 10 ? score / 10 : score;

      if (normalizedScore >= 8) return 'success';
      if (normalizedScore >= 6) return 'info';
      if (normalizedScore >= 4) return 'warning';
      return 'error';
    },

    getAlertColor(alertLevel) {
      const colorMap = {
        very_large: 'error',
        large: 'warning',
        medium: 'info',
        small: 'info',
        normal: 'grey',
      };
      return colorMap[alertLevel] || 'grey';
    },

    getGestureAlertColor(alert) {
      const colorMap = {
        very_large: 'error',
        large: 'warning',
        normal: 'success',
      };
      return colorMap[alert] || 'grey';
    },

    // Suitability methods for header
    getSuitabilityColor(score) {
      if (score >= 80) return 'success';
      if (score >= 70) return 'warning';
      return 'error';
    },

    getSuitabilityLabel(score) {
      if (score >= 80) return 'SUITABLE';
      if (score >= 70) return 'POTENTIAL FIT';
      return 'NOT SUITABLE';
    },

    getSuitabilityIcon(score) {
      if (score >= 80) return 'mdi-check-circle';
      if (score >= 70) return 'mdi-alert-circle';
      return 'mdi-close-circle';
    },

    // Video player methods
    onVideoLoaded() {
      const video = this.$refs.interviewVideo;
      if (video && isFinite(video.duration)) {
        this.duration = video.duration;
        this.isLoadingVideo = false;
        this.hasVideoError = false;
      }
    },

    onTimeUpdate() {
      const video = this.$refs.interviewVideo;
      if (video) {
        this.currentTime = video.currentTime;
      }
    },

    onVideoPlay() {
      this.isPlaying = true;
    },

    onVideoPause() {
      this.isPlaying = false;
    },

    onLoadStart() {
      this.isLoadingVideo = true;
    },

    onCanPlay() {
      const video = this.$refs.interviewVideo;
      this.isLoadingVideo = false;
      this.hasVideoError = false;

      // Update duration if not set yet
      if (video && isFinite(video.duration) && !this.duration) {
        this.duration = video.duration;
      }
    },

    onWaiting() {
      this.isLoadingVideo = true;
    },

    onVideoError(event) {
      console.error('Video error:', event);
      this.hasVideoError = true;
      this.isLoadingVideo = false;
    },

    retryVideo() {
      const video = this.$refs.interviewVideo;
      if (video) {
        this.hasVideoError = false;
        this.isLoadingVideo = true;
        video.load();
      }
    },

    togglePlayPause() {
      const video = this.$refs.interviewVideo;
      if (!video || this.hasVideoError) return;

      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },

    jumpToTime(seconds) {
      const video = this.$refs.interviewVideo;
      if (video && isFinite(seconds) && !isNaN(seconds)) {
        video.currentTime = seconds;
      }
    },

    jumpToGestureTime(timeString) {
      const seconds = this.parseTimeToSeconds(timeString);
      this.jumpToTime(seconds);
    },

    onTimelineClick(event) {
      if (this.hasVideoError || event.target.classList.contains('gesture-marker')) return;

      const video = this.$refs.interviewVideo;
      if (!video) return;

      // Si no tenemos duration de data, usa la del video directamente
      const videoDuration = this.duration || video.duration;
      if (!videoDuration || isNaN(videoDuration) || !isFinite(videoDuration)) {
        return;
      }

      const timeline = event.currentTarget.querySelector('.timeline-track');
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      const newTime = percent * videoDuration;

      if (isFinite(newTime) && !isNaN(newTime)) {
        video.currentTime = newTime;
      }
    },

    startDrag(event) {
      this.isDragging = true;
      this.clearControlsTimeout();
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      event.preventDefault();
    },

    onDrag(event) {
      if (!this.isDragging) return;

      const video = this.$refs.interviewVideo;
      if (!video) return;

      const videoDuration = this.duration || video.duration;
      if (!videoDuration || !isFinite(videoDuration)) return;

      const timeline = document.querySelector('.timeline-track');
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      const newTime = percent * videoDuration;

      if (isFinite(newTime) && !isNaN(newTime)) {
        video.currentTime = newTime;
      }
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    changePlaybackSpeed(speed) {
      const video = this.$refs.interviewVideo;
      if (video) {
        video.playbackRate = speed;
        this.playbackRate = speed;
      }
    },

    changeVolume() {
      const video = this.$refs.interviewVideo;
      if (video) {
        video.volume = this.volume / 100;
        if (this.volume > 0) {
          this.isMuted = false;
          video.muted = false;
        }
      }
    },

    toggleMute() {
      const video = this.$refs.interviewVideo;
      if (video) {
        this.isMuted = !this.isMuted;
        video.muted = this.isMuted;
      }
    },

    toggleFullscreen() {
      const container = document.querySelector('.call-player-video');
      if (!container) return;

      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      }
    },

    onFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
    },

    onMouseMove() {
      this.showControls = true;
      this.clearControlsTimeout();

      if (this.isPlaying) {
        this.controlsTimeout = setTimeout(() => {
          this.showControls = false;
        }, 3000);
      }
    },

    onMouseLeave() {
      if (this.isPlaying) {
        this.showControls = false;
      }
    },

    clearControlsTimeout() {
      if (this.controlsTimeout) {
        clearTimeout(this.controlsTimeout);
        this.controlsTimeout = null;
      }
    },

    onVolumeEnter() {
      this.clearVolumeTimeout();
      this.showVolumeSlider = true;
    },

    onVolumeLeave() {
      this.volumeHoverTimeout = setTimeout(() => {
        this.showVolumeSlider = false;
      }, 300);
    },

    onSliderEnter() {
      this.clearVolumeTimeout();
    },

    onSliderLeave() {
      this.onVolumeLeave();
    },

    onVolumeSliderInteraction() {
      this.clearVolumeTimeout();
      this.showVolumeSlider = true;
    },

    clearVolumeTimeout() {
      if (this.volumeHoverTimeout) {
        clearTimeout(this.volumeHoverTimeout);
        this.volumeHoverTimeout = null;
      }
    },

    // Analysis complete handler
    onAnalysisComplete() {
      this.fetchAnalysisData();
    },

    formatHeaderScore(value) {
      if (value == null || value === '' || Number.isNaN(Number(value))) return 'N/A';
      return `${Math.round(Number(value))}%`;
    },

    roundPercentOrNull(value) {
      if (value == null || value === '' || Number.isNaN(Number(value))) return null;
      return Math.round(Number(value));
    },
    formatDurationMinutesSeconds(totalSeconds) {
      const seconds = Number(totalSeconds);
      if (!Number.isFinite(seconds) || seconds < 0) return 'N/A';
      const minutesPart = Math.floor(seconds / 60);
      const secondsPart = Math.floor(seconds % 60);
      return `${String(minutesPart).padStart(2, '0')}:${String(secondsPart).padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
/* Candidate Header Card */
.candidate-header-card {
  border-radius: 12px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.about-text {
  color: #4f4f4f;
  font-size: 12px;
  line-height: 1.45;
}

/* Fix for shadows being clipped by parent containers */
.tabs-items-container {
  overflow: visible !important;
}

.tab-item-container {
  overflow: visible !important;
}

.raw-json-container {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  max-height: 70vh;
}

.raw-json-pre {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  max-height: 70vh;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.tabs-items-container >>> .v-window,
.tabs-items-container >>> .v-window-item,
.tabs-items-container >>> .v-window-item--active {
  overflow: visible !important;
}

/* Video player styles */
.call-player-video {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16/9;
}

.interview-video {
  width: 100%;
  height: 100%;
  object-fit: contain !important;
  display: block;
}

.loading-overlay,
.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-text,
.error-text {
  color: white;
  margin-top: 16px;
  font-size: 16px;
}

/* Video controls */
.video-controls-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    transparent 30%,
    transparent 70%,
    rgba(0, 0, 0, 0.8) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 5;
}

.video-controls-overlay.show-controls {
  opacity: 1;
  pointer-events: auto;
}

.center-controls {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
}

.play-pause-btn {
  transition: transform 0.2s ease;
}

.play-pause-btn:hover {
  transform: scale(1.1);
}

.bottom-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px 16px;
  pointer-events: auto;
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.progress-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timeline-container {
  width: 100%;
  padding: 8px 0;
  cursor: pointer;
  user-select: none;
}

.custom-timeline {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  position: relative;
  overflow: visible;
  transition: height 0.2s ease;
}

.timeline-container:hover .custom-timeline {
  height: 8px;
}

.timeline-track {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 3px;
  overflow: visible;
}

.timeline-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #6200ea;
  border-radius: 3px;
  transition: width 0.1s linear;
}

.timeline-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.timeline-thumb:hover {
  transform: translate(-50%, -50%) scale(1.2);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.5);
  cursor: grab;
}

.timeline-thumb:active {
  cursor: grabbing;
}

.gesture-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
}

.gesture-marker.marker-very_large {
  background: #d32f2f;
  box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.5);
  width: 12px;
  height: 12px;
}

.gesture-marker.marker-large {
  background: #f44336;
  box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.3);
}

.gesture-marker.marker-medium {
  background: #ff9800;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.3);
}

.gesture-marker.marker-small {
  background: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.3);
}

.controls-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-display {
  color: white;
  font-size: 12px;
  font-weight: 500;
  min-width: 110px;
}

.speed-control {
  display: flex;
  align-items: center;
}

.speed-btn {
  min-width: 50px;
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.volume-slider-container {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  padding: 12px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.volume-slider {
  writing-mode: bt-lr;
  -webkit-appearance: slider-vertical;
  width: 8px;
  height: 80px;
  background: transparent;
  outline: none;
}

.fullscreen-corner {
  pointer-events: auto;
}

/* Transcription styles */
.transcription-card {
  display: flex;
  flex-direction: column;
}

.transcription-content {
  flex: 1;
  overflow-y: auto;
  max-height: 540px;
}

.transcription-message {
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
}

.avatar-message {
  background: #e3f2fd;
  border-left: 3px solid #2196f3;
}

.candidate-message {
  background: #f1f8e9;
  border-left: 3px solid #4caf50;
}

.message-header {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #555;
}

.message-content {
  font-size: 12px;
  color: #333;
  line-height: 1.5;
}

.timestamp-badge {
  background: #6200ea;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  min-width: 50px;
  text-align: center;
}

.segment-content {
  font-size: 12px;
}

.speaking-indicator {
  color: #4caf50;
  font-weight: 500;
}

.silent-indicator {
  color: #9e9e9e;
  font-style: italic;
}

/* Code blocks */
.code-block {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  max-height: 400px;
  overflow-y: auto;
}

/* Report text */
.report-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.6;
  color: #333;
  background: transparent;
  margin: 0;
  padding: 0;
}

/* Utility classes */
.cursor-pointer {
  cursor: pointer;
}
</style>
